/* eslint-disable sonarjs/cognitive-complexity */
import { Form, Formik, Field } from 'formik';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import { XumoButton, Button, FormField, IFormField } from '.';
import { InputType, PageContext, useActiveElement } from '..';
import { ToastsContext } from '../contexts/ToastContext';
import { useDirectusAPI } from '../hooks';
import { useFormSuccessTracking, useFormErrorTracking } from '../hooks/useAdobeAnalytics';
import { CTA } from '../types';
import { chunkFormInput } from '../utils';
import ReactModal from 'react-modal';
import { motion, AnimatePresence } from 'framer-motion';

type FormProps = {
  formFields: IFormField[];
  CTA: CTA;
  formAction: handleSubmitProps;
  formName?: string;
  styleOverrides?: {
    form?: string;
    formField?: string;
    CTA?: {
      general?: string;
      disabledColor?: string;
      enabledColor?: string;
      alignment?: string;
      padding?: string;
      border_radius?: string;
    };
    accentColor?: string;
    hoverColor?: string;
    hideLabel?: boolean;
  };
  showTextLengthCount?: boolean;
};

export type handleSubmitProps = {
  submissionEndpoint: string;
  successTitle?: string;
  successMessage?: string;
  errorMessage?: string;
  onClick?: () => void;
  onSuccess?: () => void;
  formType?: string;
};

const ErrorText = ({ text, id }: { text?: string; id: string }) => {
  return (
    <p id={id} className="relative bottom-0 left-4 text-sm italic text-xumoBerry" data-i18n>
      {text}
    </p>
  );
};

const TextLengthCount = ({ textLength, maxLength }: { textLength: number; maxLength?: number }) => (
  <p className={`${textLength > 120 ? 'text-xumoBerry' : 'text-xumoCharcoal'} justify-self-end px-4 pt-4 text-sm`}>
    {textLength} {maxLength && `/ ${maxLength}`}
  </p>
);

const modalVariants = {
  initial: { scale: 1.1, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.9, opacity: 0 },
};

export const XumoForm = ({ formFields, CTA, formAction, formName, styleOverrides, showTextLengthCount = true }: FormProps) => {
  const [modalStatus, setModalStatus] = useState<string | undefined>();
  const focusedElement = useActiveElement();
  const router = useRouter();
  const api = useDirectusAPI();
  const { addToast } = useContext(ToastsContext);
  const { pageCtx } = useContext(PageContext);
  const { host } = pageCtx;
  const { trackFormSuccess } = useFormSuccessTracking();
  const { trackFormError } = useFormErrorTracking();

  // useEffect(() => {
  //   setModalStatus('LOADING')
  //   window.modalLoading = () => setModalStatus('LOADING')
  //   window.modalSuccess = () => setModalStatus('SUCCESS')
  // }, [])

  const sanitizedFieldNameMap = useMemo<Record<string, string>>(
    () => formFields.reduce((acc, { name }) => ({ ...acc, [name]: name.toLowerCase().replace(/ /g, '_') }), {}),
    [formFields],
  );
  const initFormValues = useMemo(
    () =>
      formFields.reduce(
        (acc: { [key: string]: string }, { name }) => {
          acc[sanitizedFieldNameMap[name]] = '';
          return acc;
        },
        { extra_field: '' },
      ),
    [sanitizedFieldNameMap, formFields],
  );

  const [ariaIds, setAriaIds] = useState<{ [key: string]: string }>({});
  useEffect(() => {
    const newAriaIds = formFields.reduce((acc: { [key: string]: string }, { name }) => {
      acc[sanitizedFieldNameMap[name]] = uuid();
      return acc;
    }, {});

    setAriaIds(newAriaIds);
  }, [formFields, sanitizedFieldNameMap]);

  const validationSchema = yup.object(
    // Dynamically generate validation schema based on formInput type
    formFields?.reduce((acc: { [key: string]: yup.StringSchema }, { name, type, placeholder, customValidation }) => {
      let validation: yup.StringSchema;
      if (customValidation) {
        validation = customValidation;
      } else {
        switch (type) {
          case InputType.Textarea:
            validation = yup.string().required(`${placeholder} is required`).max(120, `${placeholder} can be at most 120 characters`);
            break;
          case InputType.Email:
            validation = yup.string().required(`${placeholder} is required`).email(`${placeholder} must be a valid email`);
            break;
          case InputType.Tel:
            validation = yup
              .string()
              .required(`${placeholder} is required`)
              .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im, `${placeholder} must be a valid phone number`);
            break;
          case InputType.Checkbox:
            validation = yup.string().required(`This field is required`);
            break;
          case InputType.Radio:
          default:
            validation = yup.string().required(`${placeholder} is required`);
        }
      }

      acc[sanitizedFieldNameMap[name]] = validation;
      return acc;
    }, {}),
  );

  const handleSubmit = async (formData: Record<string, string>) => {
    try {
      setModalStatus('LOADING');
      const res = await fetch(formAction?.submissionEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          slug: host + router.asPath,
          formType: formAction?.formType
        }),
      });

      if (res.status === 200) {
        setModalStatus('SUCCESS');
        // Track successful form submission
        trackFormSuccess({
          formName: formName || formAction.submissionEndpoint.split('/').pop() || 'form-submission',
          formData,
          page: host + router.asPath,
          buttonText: CTA.label,
        });
      }
      return res.status;
    } catch (err) {
      console.error(err);
      setModalStatus(undefined);
      addToast({ id: uuid(), title: '', description: formAction?.errorMessage || "Sorry, we couldn't submit your request.", type: 'error' });
    }
  };

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={initFormValues}
        onSubmit={async (values, actions) => {
          actions.setSubmitting(true);
          actions.validateForm();
          formAction.onClick && formAction.onClick();
          await handleSubmit(values);
          actions.resetForm();
        }}
      >
        {({ values, errors, dirty, isValid, isSubmitting, handleChange, handleSubmit }) => {
          // Log validation errors when they occur - only once when errors change
          useEffect(() => {
            if (Object.keys(errors).length > 0) {
              const errorMessages = Object.values(errors).join(' | ');
              console.log('Form validation errors:', errorMessages);

              // Track form errors using the new tracking function
              trackFormError({
                formName: formName || formAction.submissionEndpoint.split('/').pop() || 'form-submission',
                errorMessage: errorMessages,
                page: host + router.asPath,
              });
            }
          }, [errors, trackFormError, formName, formAction.submissionEndpoint, host, router.asPath]);

          const keys = Object.keys(values);

          let hasEmptyFields = false;

          for (let i = 0; i < keys.length; i++) {
            if (values[keys[i]] === '' && keys[i] !== 'extra_field') {
              hasEmptyFields = true;
              break;
            }
          }

          const isDisabled = isSubmitting || !dirty || hasEmptyFields;

          return (
            <Form key={'lead-gen-form'} className={`form z-20 flex w-full flex-col gap-4 [filter:revert] ${styleOverrides?.form}`} onSubmit={handleSubmit}>
              {chunkFormInput(formFields, 2).map((row: IFormField | IFormField[], index) => {
                if (Array.isArray(row)) {
                  return (
                    <div key={'form' + index} className="w-full">
                      <div className="flex w-full flex-col justify-between gap-4 sm:flex-row">
                        {row.map(({ type, name, placeholder, width }: IFormField, j) => {
                          const sanitizedName = sanitizedFieldNameMap[name];
                          return (
                            <FormField
                              key={`${index}-${j}`}
                              name={sanitizedName}
                              placeholder={placeholder}
                              type={type}
                              handleChange={handleChange}
                              value={values[name]}
                              focusedElementId={focusedElement?.id}
                              styleOverride={styleOverrides?.formField}
                              hideLabel={styleOverrides?.hideLabel}
                              width={width}
                              errorId={ariaIds[sanitizedName] + `-${j}`}
                              errorText={errors[sanitizedName]}
                              accentColor={styleOverrides?.accentColor}
                              hoverColor={styleOverrides?.hoverColor}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const sanitizedName = sanitizedFieldNameMap[row.name];
                const ariaId = ariaIds[sanitizedName];

                return (
                  <div key={'form-field' + index} className="w-full">
                    <FormField
                      name={sanitizedName}
                      placeholder={row.placeholder}
                      type={row.type}
                      handleChange={handleChange}
                      value={values[sanitizedName]}
                      focusedElementId={focusedElement?.id}
                      styleOverride={styleOverrides?.formField}
                      hideLabel={styleOverrides?.hideLabel}
                      width={row.width}
                      errorId={ariaId}
                      errorText={errors[sanitizedName]}
                      accentColor={styleOverrides?.accentColor}
                      hoverColor={styleOverrides?.hoverColor}
                    />
                    {row.type === InputType.Textarea && showTextLengthCount && <TextLengthCount textLength={values[sanitizedName].length} maxLength={120} />}
                  </div>
                );
              })}
              <div className="absolute bottom-0 right-0 opacity-0">
                <Field
                  type="text"
                  name="extra_field"
                  id="extra_field"
                  tabIndex="-1"
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                  onChange={handleChange}
                />
              </div>
              <XumoButton submit isDisabled={isDisabled} label={CTA.label} alignment={styleOverrides?.CTA?.alignment} color={styleOverrides?.accentColor} className={styleOverrides?.CTA?.general} padding={styleOverrides?.CTA?.padding} border_radius={styleOverrides?.CTA?.border_radius} />
            </Form>
          );
        }}
      </Formik>

      <ReactModal isOpen={!!modalStatus} style={{ overlay: { zIndex: 1000 }, content: { inset: '0', background: 'transparent', padding: 0, margin: 0 } }}>
        <div className={'relative flex h-full w-full flex-col items-center justify-center bg-[#00000088]'}>
          <AnimatePresence>
            {modalStatus === 'LOADING' && (
              <motion.div
                key="modal-loading"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={modalVariants}
                className="absolute m-2 flex rounded-lg bg-white p-4"
              >
                Sending your request...
              </motion.div>
            )}
            {modalStatus === 'SUCCESS' && (
              <motion.div
                key="modal-success"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={modalVariants}
                className="absolute m-4 flex h-96 w-full flex-col items-center justify-center rounded-lg bg-white p-4 text-center md:w-2/3"
                data-i18n
              >
                <h2 className="type-heading mt-4 p-4 text-center" data-i18n>
                  {formAction?.successTitle || 'Thank you!'}
                </h2>
                <p className="p-4 md:w-2/5" data-i18n>
                  {formAction?.successMessage ||
                    'Thank you for contacting us! Our team has received your inquiry and will connect with you after reviewing and assessing the potential business opportunity.'}
                </p>
                <Button
                  buttonType="submit-primary"
                  label={'Okay'}
                  onClick={() => {
                    setModalStatus(undefined);
                    if (formAction?.onSuccess) {
                      formAction.onSuccess();
                    }
                  }}
                  styleOverrides={{
                    css: { backgroundColor: '#000', color: '#FFF', marginTop: 15 },
                    tailwind: 'mb-4',
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ReactModal>
    </>
  );
};
